/**
 * I codici tasto, in un posto solo.
 *
 * TERZA GIUNZIONE (01-ARCHITETTURA §2.4, punto 3). «Se i codici stanno sparsi nelle
 * schermate, ogni piattaforma nuova li ritrova sparsi.» Qui c'è una tabella per
 * piattaforma e nient'altro: aggiungere Tizen vuol dire riempire una riga, non cercare.
 *
 * Due chiavi per riga, perché i telecomandi non sono d'accordo fra loro: `key` (il valore
 * di `KeyboardEvent.key`, che i browser moderni riempiono) e `code` (il numerico
 * `keyCode`, che è l'unico che i telecomandi delle TV mandano davvero).
 */

import type { RemoteKey, RemotePlatform } from '@player/ports';

export interface KeyMap {
  /** Da `KeyboardEvent.key` al tasto del telecomando. */
  readonly byKey: Readonly<Record<string, RemoteKey>>;
  /** Da `KeyboardEvent.keyCode` al tasto del telecomando. */
  readonly byCode: Readonly<Record<number, RemoteKey>>;
  /**
   * `false` quando la tabella non è stata compilata per questa piattaforma.
   * Selezionarla solleva invece di ripiegare su quella del browser: un telecomando che
   * «quasi» funziona è il guasto che nessuno riesce a riprodurre.
   *
   * Oggi sono tutte e quattro riempite. Il campo resta perché una piattaforma nuova nasce vuota, e
   * va rotta al primo avvio e non alla terza freccia premuta.
   */
  readonly filled: boolean;
  /** Il telecomando ha un puntatore che si comporta come un mouse (LG). */
  readonly hasPointer: boolean;
}

const DIRECTIONS_BY_KEY: Readonly<Record<string, RemoteKey>> = {
  ArrowUp: { kind: 'direction', direction: 'up' },
  ArrowDown: { kind: 'direction', direction: 'down' },
  ArrowLeft: { kind: 'direction', direction: 'left' },
  ArrowRight: { kind: 'direction', direction: 'right' },
};

const DIRECTIONS_BY_CODE: Readonly<Record<number, RemoteKey>> = {
  37: { kind: 'direction', direction: 'left' },
  38: { kind: 'direction', direction: 'up' },
  39: { kind: 'direction', direction: 'right' },
  40: { kind: 'direction', direction: 'down' },
};

const browser: KeyMap = {
  byKey: {
    ...DIRECTIONS_BY_KEY,
    Enter: { kind: 'select' },
    ' ': { kind: 'select' },
    Escape: { kind: 'back' },
    Backspace: { kind: 'erase' },
  },
  byCode: { ...DIRECTIONS_BY_CODE, 13: { kind: 'select' }, 27: { kind: 'back' }, 8: { kind: 'erase' } },
  filled: true,
  hasPointer: true,
};

/**
 * Android TV, Google TV, Fire TV e i box AOSP, dentro una WebView.
 *
 * Il tasto Indietro arriva con `keyCode` 4 su Android; le WebView lo consegnano anche
 * come `Escape`. Entrambi vanno riconosciuti, perché sui box non certificati la WebView è
 * quella del giorno in cui il box è uscito e non si sa quale delle due forme userà
 * (01-ARCHITETTURA §2.5).
 */
const androidTv: KeyMap = {
  byKey: {
    ...DIRECTIONS_BY_KEY,
    Enter: { kind: 'select' },
    Escape: { kind: 'back' },
    GoBack: { kind: 'back' },
    Backspace: { kind: 'erase' },
  },
  byCode: {
    ...DIRECTIONS_BY_CODE,
    13: { kind: 'select' },
    23: { kind: 'select' }, // KEYCODE_DPAD_CENTER
    66: { kind: 'select' }, // KEYCODE_ENTER
    4: { kind: 'back' }, // KEYCODE_BACK
    27: { kind: 'back' },
    67: { kind: 'erase' }, // KEYCODE_DEL
  },
  filled: true,
  hasPointer: false,
};

/**
 * Samsung Tizen — **dai modelli 2019** (Tizen 5.0, Chromium 63).
 *
 * ## Molto meno esotico di quanto il timore dicesse
 *
 * Qui c'era scritto che Tizen ha «codici tasto propri che vanno perfino registrati». Metà vero, e la
 * metà che conta è falsa: frecce ed Enter sono **gli stessi del browser**, e l'unico codice proprio
 * è l'Indietro, `10009`. La registrazione con `tizen.tvinputdevice.registerKey` serve per i tasti
 * colorati, il volume e i comandi di riproduzione — questi sei Tizen li consegna da sé, senza
 * chiedere niente.
 *
 * ## L'Indietro passa dal codice, non dal nome
 *
 * `event.key` su Tizen porta i nomi DOM standard per i tasti che li hanno (`ArrowUp`, `Enter`), ma
 * per l'Indietro il nome non è standard e cambia fra piattaforme. `translateKey` guarda prima `key`
 * e poi `keyCode`: tenere l'Indietro nel solo `byCode` è l'unica forma che non dipende da un nome
 * che non è specificato da nessuno.
 *
 * ## Niente `erase`, e non è una dimenticanza
 *
 * Su un telecomando da televisore **non c'è un tasto che cancella**. Si cancella dal tastierino a
 * schermo, che `PinPad` fornisce. Mettere qui un codice qualunque darebbe un tasto che su un
 * apparecchio fa qualcosa e su un altro no.
 *
 * Il pavimento (Tizen 5.0) va dichiarato anche in `required_version` del `config.xml`, dove
 * l'apparecchio lo può leggere: vedi `docs/01-ARCHITETTURA.md` §2.6.
 */
const tizen: KeyMap = {
  byKey: { ...DIRECTIONS_BY_KEY, Enter: { kind: 'select' } },
  byCode: {
    ...DIRECTIONS_BY_CODE,
    13: { kind: 'select' },
    10009: { kind: 'back' }, // il solo codice proprio di Tizen
  },
  filled: true,
  hasPointer: false,
};

/**
 * LG webOS — **dai modelli 2020** (webOS 5.0, Chromium 68).
 *
 * ## Il pavimento è un anno più alto di Samsung, e non per scelta
 *
 * Lo stesso livello di compilazione compra Tizen dal 2019 e webOS solo dal 2020, perché il
 * webOS 4.x dei modelli 2018-2019 è fermo a Chromium 53. Chi dice «dal 2019 in poi» sbaglia su LG.
 *
 * E su webOS **non esiste un campo dove dichiararlo**: `appinfo.json` non ha un minimo di versione,
 * quindi l'unico posto dove dirlo è la scheda del negozio. Un'installazione a mano su un webOS 4 dà
 * uno schermo bianco senza spiegazioni (`docs/01-ARCHITETTURA.md` §2.6).
 *
 * ## I codici sono quelli di Tizen con un altro Indietro
 *
 * Frecce 37-40 ed Enter 13 come tutti; l'Indietro è `461`.
 *
 * ## `hasPointer` è vero, e un booleano non basta
 *
 * Il Magic Remote ha due modalità: a cinque vie, senza cursore, e a puntatore, dove un cursore si
 * muove a schermo e manda eventi del mouse normali. Il passaggio fra le due lo annuncia l'evento
 * `cursorStateChange`, che **oggi nessuno ascolta**: in modalità puntatore si vedono due fuochi
 * insieme — la cornice di `useSpatialFocus` ferma dov'era, più il cursore. È lavoro a parte, e il
 * booleano qui non lo risolve: lo segnala.
 */
const webos: KeyMap = {
  byKey: { ...DIRECTIONS_BY_KEY, Enter: { kind: 'select' } },
  byCode: {
    ...DIRECTIONS_BY_CODE,
    13: { kind: 'select' },
    461: { kind: 'back' }, // il solo codice proprio di webOS
  },
  filled: true,
  hasPointer: true,
};

const KEY_MAPS: Readonly<Record<RemotePlatform, KeyMap>> = { browser, androidTv, tizen, webos };

export function keyMapFor(platform: RemotePlatform): KeyMap {
  const map = KEY_MAPS[platform];
  if (!map.filled) {
    throw new Error(
      `keyMapFor: la tabella dei codici tasto per "${platform}" non è stata compilata. ` +
        'Va riempita dalle tabelle correnti dell\'SDK, non a memoria.',
    );
  }
  return map;
}

/** Utile alle prove e al banco di misura: dice quali piattaforme sono pronte. */
export function filledPlatforms(): ReadonlyArray<RemotePlatform> {
  return (Object.keys(KEY_MAPS) as RemotePlatform[]).filter((name) => KEY_MAPS[name].filled);
}

/**
 * Traduce un evento di tastiera in un tasto del telecomando.
 *
 * Il carattere digitato è l'ultima risorsa e vale solo nelle schermate che lo accettano
 * — accesso, ricerca, PIN: «se c'è una tastiera collegata, i caratteri arrivano
 * direttamente al campo senza passare dalla tastiera a schermo» (03-SCHERMATE-TV §1).
 */
export function translateKey(
  map: KeyMap,
  event: { key: string; keyCode: number; ctrlKey: boolean; altKey: boolean; metaKey: boolean },
): RemoteKey | null {
  if (event.ctrlKey || event.altKey || event.metaKey) return null;
  const byKey = map.byKey[event.key];
  if (byKey) return byKey;
  const byCode = map.byCode[event.keyCode];
  if (byCode) return byCode;
  if (event.key.length === 1) return { kind: 'character', character: event.key };
  return null;
}
